import { isDevice4GMeter } from './deviceMeter';
import { createMqttWS } from './mqttWS';

/** 与 index4g yjRemoteMqttRxWaitMsByMappingTier 一致 */
export const YJ_REMOTE_MQTT_WAIT_MS_TIER1 = 8000;
export const YJ_REMOTE_MQTT_WAIT_MS_TIER2 = 16000;
/** 仅重启等 52：第二台回包可能到 ~25s，与抄表档位无关 */
export const YJ_REMOTE_REBOOT_ACK_WAIT_MS = 30000;

export function yjRemoteMqttRxWaitMsByMappingTier(mappingFanoutTier) {
  return Number(mappingFanoutTier) === 2 ? YJ_REMOTE_MQTT_WAIT_MS_TIER2 : YJ_REMOTE_MQTT_WAIT_MS_TIER1;
}

/** QK/NQK：表号参与指令拼接；4G/YJ 等优先用 MAC 查映射（与小程序 test1 一致） */
export function isQKOrNQKMeter(meterNo) {
  const m = String(meterNo || '').trim().toUpperCase();
  return m.startsWith('QK') || m.startsWith('NQK');
}

/** 下发 MQTT / publish-by-meter 时使用的 meter_no（与 device_topic_mapping 一致） */
export function getAdminMeterPublishKey(device) {
  if (!device) return '';
  const is4G = isDevice4GMeter(device);
  let mac = String(device.device_mac || '').trim();
  if (mac.includes(':')) mac = mac.replace(/:/g, '');
  const meterNo = String(device.current_meter_no || '').trim();
  const useMac = (is4G || !isQKOrNQKMeter(meterNo)) && mac;
  const key = useMac ? mac : meterNo;
  return key || String(device.factory_meter_no || '').trim();
}

function crc8(buffer) {
  let crc = 0x00;
  for (let i = 0; i < buffer.length; i++) {
    crc ^= buffer[i];
    for (let b = 0; b < 8; b++) {
      if (crc & 0x80) crc = (crc << 1) ^ 0x07;
      else crc <<= 1;
      crc &= 0xff;
    }
  }
  return crc & 0xff;
}

export function hexToBytes(hexStr) {
  const clean = String(hexStr || '').replace(/\s+/g, '').toLowerCase();
  if (clean.length % 2 !== 0) return null;
  const bytes = [];
  for (let i = 0; i < clean.length; i += 2) {
    const byte = parseInt(clean.substr(i, 2), 16);
    if (Number.isNaN(byte)) return null;
    bytes.push(byte);
  }
  return bytes;
}

export function buildFinalPayload(rawHex, autoCrc = true) {
  const clean = String(rawHex || '').replace(/\s+/g, '').toLowerCase();
  const bytes = hexToBytes(clean);
  if (!bytes) throw new Error('指令格式不合法（需偶数位16进制字符）');
  if (!autoCrc) return clean;
  const crc = crc8(bytes);
  const crcHex = crc.toString(16).padStart(2, '0');
  return clean + crcHex;
}

/** 与 test1：非 BLE 直连时 meterNo 须为 12 位十六进制表号地址 */
export function remotePayloadUseBleDirect(meterKey) {
  const m = String(meterKey || '').trim();
  return !/^[0-9a-fA-F]{12}$/i.test(m);
}

export function buildRemoteReadHex(meterNo, isBleDirect) {
  const m = String(meterNo || '').trim();
  if (!isBleDirect && !/^[0-9a-fA-F]{12}$/.test(m)) {
    throw new Error('表号不合法（非 BLE 直连时需 12 位十六进制地址）');
  }
  return ((isBleDirect ? '' : m) + '51' + '02').toLowerCase();
}

/** 写入剩余电量/水量（绝对值，单位：度或吨） */
export function buildRemoteSetPowerHex(meterNo, power, isBleDirect) {
  const m = String(meterNo || '').trim();
  if (!isBleDirect && !/^[0-9a-fA-F]{12}$/.test(m)) {
    throw new Error('表号不合法（非 BLE 直连时需 12 位十六进制地址）');
  }
  const v = Math.max(0, Number(power) || 0);
  const vInt = Math.round(v * 1000);
  const valueHex = vInt.toString(16).padStart(8, '0');
  return ((isBleDirect ? '' : m) + '45' + '02' + valueHex).toLowerCase();
}

/** 开闸/关闸（与 test4 buildSwitchCommandHex 一致，不含 CRC） */
export function buildRemoteSwitchHex(meterNo, action, isBleDirect) {
  const m = String(meterNo || '').trim();
  if (!isBleDirect && !/^[0-9a-fA-F]{12}$/.test(m)) {
    throw new Error('表号不合法（非 BLE 直连时需 12 位十六进制地址）');
  }
  const p3 = action === 'close' ? '02' : '01';
  return ((isBleDirect ? '' : m) + '57' + '02' + p3).toLowerCase();
}

/** 重启设备（与 test4 chongqi 一致，不含 CRC） */
export function buildRemoteRebootHex(meterNo, isBleDirect) {
  const m = String(meterNo || '').trim();
  if (!isBleDirect && !/^[0-9a-fA-F]{12}$/.test(m)) {
    throw new Error('表号不合法（非 BLE 直连时需 12 位十六进制地址）');
  }
  return ((isBleDirect ? '' : m) + '52' + '02').toLowerCase();
}

/** 12 位 hex 发布键归一化 */
export function normalizePublishKey12(raw) {
  let v = String(raw || '').trim().toLowerCase().replace(/:/g, '');
  if (v.length >= 12 && /^[0-9a-f]{12}/.test(v)) return v.slice(0, 12);
  return '';
}

/**
 * 从 reboot-related-mains 响应挑选「必须回包」的 publish_key：
 * - 主表：role=self
 * - 副表：最新 rx_topics[0] 上 role=main 的全部（每主题后端已 ≤2）
 */
export function pickMustAckPublishKeysFromRebootData(data) {
  const d = data || {};
  const targets = Array.isArray(d.targets) ? d.targets : [];
  const topics = Array.isArray(d.rx_topics) ? d.rx_topics.map((t) => String(t || '').trim()).filter(Boolean) : [];
  const isMain = !!d.is_main;
  if (isMain) {
    return targets
      .filter((t) => String(t?.role || '') === 'self')
      .map((t) => normalizePublishKey12(t.publish_key))
      .filter(Boolean);
  }
  const latest = topics[0] || '';
  if (!latest) return [];
  return targets
    .filter(
      (t) =>
        String(t?.role || '') === 'main' &&
        String(t?.source_rx_topic || '').trim() === latest
    )
    .map((t) => normalizePublishKey12(t.publish_key))
    .filter(Boolean);
}

/** 解析 MQTT 下行 52 重启回调；allowedKeys 为空则不按地址过滤 */
export function parseMqtt52RebootResponse(rawMsg, allowedKeys) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 18) return null;
  const callbackMeterNo = msg.substring(0, 12);
  if (!/^[0-9a-f]{12}$/.test(callbackMeterNo)) return null;
  if (msg.substring(12, 14) !== '52') return null;
  const keys = (allowedKeys || []).map((k) => normalizePublishKey12(k)).filter(Boolean);
  if (keys.length > 0 && !keys.includes(callbackMeterNo)) return null;
  const ackHex = msg.substring(16, 18);
  const ackDec = ackHex ? parseInt(ackHex, 16) : -1;
  return {
    key: callbackMeterNo,
    ackHex,
    ackDec,
    ok: ackDec === 0,
    raw: msg,
  };
}

export function getRxAddressCandidates(device) {
  const set = new Set();
  const add = (raw) => {
    let v = String(raw || '').trim().toLowerCase().replace(/:/g, '');
    if (v.length >= 12 && /^[0-9a-f]{12}/.test(v)) set.add(v.slice(0, 12));
  };
  if (!device) return [];
  add(device.device_mac);
  add(device.current_meter_no);
  add(device.factory_meter_no);
  return Array.from(set);
}

/** 8 位 hex 作 32 位有符号大端整数（千分之一度），与小程序 yjMeterEnergyHex8ToSignedMilli 一致 */
function yjMeterEnergyHex8ToSignedMilli(hex8) {
  const h = String(hex8 || '').trim();
  if (h.length !== 8) return 0;
  const n = parseInt(h, 16);
  if (!Number.isFinite(n)) return 0;
  return n | 0;
}

/** 与 index4g _mqttRemainTotalLookSane */
export function mqttRemainTotalLookSane(remain, total) {
  const r = Number(remain);
  const t = Number(total);
  const maxKwh = 200000;
  if (!Number.isFinite(r) || !Number.isFinite(t)) return false;
  if (r < -1 || t < -1) return false;
  if (r > maxKwh || t > maxKwh) return false;
  return true;
}

/** 从 51/57/54 原始回包解析开关闸（01=通电，02=断电） */
export function parseSwitchStatusFromRaw(rawMsg) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 18) return null;
  const parseEmsw = (hex2) => {
    const n = parseInt(hex2, 16);
    if (n === 1) return '通电';
    if (n === 2) return '断电';
    return null;
  };
  if (msg.substring(12, 14) === '57') return parseEmsw(msg.substring(16, 18));
  if (msg.length >= 60 && msg.substring(12, 14) === '51') return parseEmsw(msg.substring(58, 60));
  if (msg.substring(12, 14) === '54' && msg.length >= 76) return parseEmsw(msg.slice(-6, -4));
  return null;
}

/** 解析 MQTT 下行 51 抄表回调（与 index4g parseRxTopicMessage 一致） */
export function parseMqtt51ReadResponse(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 34) return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  if (msg.substring(12, 14) !== '51') return null;
  const totalRaw = yjMeterEnergyHex8ToSignedMilli(msg.substring(18, 26));
  const remainRaw = yjMeterEnergyHex8ToSignedMilli(msg.substring(26, 34));
  const remain = remainRaw / 1000;
  const total = totalRaw / 1000;
  if (!mqttRemainTotalLookSane(remain, total)) return null;
  const switchStatus = parseSwitchStatusFromRaw(msg) || '';
  return { remain, total, switchStatus };
}

/** 档 1：仅 primary rx；档 2：primary + candidates 去重（与 index4g _yjRemoteMqttRxTopicsForHttpPoll） */
export function rxTopicsForHttpPoll(mapData) {
  const primary = String(mapData?.rx_topic || '').trim();
  if (!primary) return [];
  if (Number(mapData?.mapping_fanout_tier) !== 2) return [primary];
  const seen = new Set([primary]);
  const out = [primary];
  const cand = mapData?.candidates || [];
  for (let i = 0; i < cand.length; i++) {
    const rx = String(cand[i]?.rx_topic || '').trim();
    if (!rx || seen.has(rx)) continue;
    seen.add(rx);
    out.push(rx);
  }
  return out;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function shouldAcceptPolledRaw({ raw, lastRaw, publishAt, msgRes }) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return { accept: false, trimmed: '' };
  if (lastRaw && trimmed === lastRaw) {
    const ua = msgRes?.data?.updated_at;
    if (ua && publishAt) {
      const ut = Date.parse(ua);
      if (!Number.isNaN(ut) && ut >= publishAt - 500) return { accept: true, trimmed };
    }
    return { accept: false, trimmed };
  }
  return { accept: true, trimmed };
}

/**
 * 管理端远程 51 抄表（对齐 index4g remoteReadMeter：QoS1、档位超时、WS+HTTP、多 RX、sent_at 去重）
 * @returns {Promise<{ remain: number, total: number } | null>}
 */
export async function adminRemoteRead51({
  device,
  getMqttMappingByMeter,
  mqttPublishByMeter,
  getMqttLatestMessage,
  useWebSocket = true,
}) {
  const publishKey = getAdminMeterPublishKey(device);
  if (!publishKey) throw new Error('无法确定表号或 MAC，请检查设备档案');

  const mapRes = await getMqttMappingByMeter(publishKey);
  if (!mapRes?.success || !mapRes?.data?.rx_topic) {
    throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
  }
  const mapData = mapRes.data;
  const tier = Number(mapData.mapping_fanout_tier) === 2 ? 2 : 1;
  const waitMs = yjRemoteMqttRxWaitMsByMappingTier(tier);
  const rxTopics = rxTopicsForHttpPoll(mapData);
  const addressCandidates = getRxAddressCandidates(device);
  const isBleDirect = remotePayloadUseBleDirect(publishKey);
  const payload = buildFinalPayload(buildRemoteReadHex(publishKey, isBleDirect), true);
  const publishAt = Date.now();
  const pollOpts = buildMqttMessagePollOpts(publishKey, 'read', publishAt);

  const tryPublish = async (mk) => {
    const pubRes = await mqttPublishByMeter({ meter_no: mk, payload, qos: 1 });
    if (!pubRes?.success) {
      throw new Error(pubRes?.msg || pubRes?.error || '抄表指令发送失败');
    }
  };

  try {
    await tryPublish(publishKey);
  } catch (e) {
    const msg = String(e?.message || e);
    const alt = String(device?.current_meter_no || device?.factory_meter_no || '').trim();
    if (/未找到该表号/.test(msg) && alt && alt.toLowerCase() !== String(publishKey).trim().toLowerCase()) {
      await tryPublish(alt);
    } else {
      throw e;
    }
  }

  let parsedFromWs = null;
  let wsHandle = null;
  const tryParse = (raw) => parseMqtt51ReadResponse(raw, addressCandidates);

  if (useWebSocket && mapData.rx_topic) {
    const extraRx = tier === 2 ? rxTopics.filter((t) => t !== mapData.rx_topic) : [];
    wsHandle = createMqttWS(
      mapData.rx_topic,
      (_topic, msg, hex) => {
        const raw = String(hex || msg || '').trim();
        const p = tryParse(raw);
        if (p) parsedFromWs = p;
      },
      { extraRxTopics: extraRx }
    );
    try {
      await Promise.race([wsHandle.ready, sleep(3000)]);
    } catch (_) {
      wsHandle = null;
    }
  }

  const deadline = Date.now() + waitMs;
  let lastRaw = '';
  while (Date.now() < deadline) {
    if (parsedFromWs) {
      wsHandle?.close();
      return parsedFromWs;
    }

    const pairs = await Promise.all(
      rxTopics.map(async (topic) => {
        try {
          const msgRes = await getMqttLatestMessage(topic, 'raw', pollOpts);
          return { msgRes };
        } catch {
          return { msgRes: null };
        }
      })
    );

    for (let i = 0; i < pairs.length; i++) {
      const { msgRes } = pairs[i];
      const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
      const { accept, trimmed } = shouldAcceptPolledRaw({ raw, lastRaw, publishAt, msgRes });
      if (!accept) continue;
      lastRaw = trimmed;
      const p = tryParse(trimmed);
      if (p) {
        wsHandle?.close();
        return p;
      }
    }
    await sleep(400);
  }

  wsHandle?.close();
  return null;
}

/** 与小程序 GET /api/mqtt/message 一致：meter_no、op、sent_at；多 rx 聚合由服务端按 device_info 档位解析，不必传 mapping_fanout_tier */
export function buildMqttMessagePollOpts(publishKey, op, sentAtMs) {
  const mk = String(publishKey || '').trim().toLowerCase().replace(/:/g, '');
  const opts = { op: op || '' };
  if (/^[0-9a-f]{12}$/.test(mk)) opts.meter_no = mk;
  const n = Number(sentAtMs);
  if (sentAtMs != null && Number.isFinite(n)) opts.sent_at = String(Math.floor(n));
  return opts;
}

/**
 * 收集一批表的 52 回包状态（不下发；不因个别失败/超时而抛错）。
 * 支持 afterListenReady：WS 就绪后再下发，避免「听晚了」丢快回包。
 * HTTP：按「主题 × 仍 pending 的表」轻量轮询（默认 600ms），收齐/超时/abort 必关 WS。
 * status: pending | ok | fail | timeout
 */
export async function adminCollectReboot52Statuses({
  watchKeys,
  rxTopics: rxTopicsIn,
  getMqttMappingByMeter,
  getMqttLatestMessage,
  waitMs = YJ_REMOTE_REBOOT_ACK_WAIT_MS,
  publishAt = Date.now(),
  useWebSocket = true,
  httpPollMs = 600,
  onUpdate,
  shouldAbort,
  /** WS 就绪后调用；可 return { watchKeys, rxTopics } 合并进监听集（用于先听再下发） */
  afterListenReady,
}) {
  const needList = [];
  const needSet = new Set();
  const byKey = {};
  /** 下发完成前尚不知全部 watchKeys：先把任意 52 按 key 暂存，合并目标后再套用 */
  const earlyBuf = {};

  const emit = (key) => {
    if (typeof onUpdate === 'function' && byKey[key]) {
      onUpdate({ key, ...byKey[key], byKey: { ...byKey } });
    }
  };

  const applyAck = (k, ack) => {
    if (!byKey[k] || byKey[k].status !== 'pending') return;
    byKey[k] = {
      status: ack.ok ? 'ok' : 'fail',
      ackDec: ack.ackDec,
      ackHex: ack.ackHex || '',
    };
    emit(k);
  };

  const ensureKey = (raw) => {
    const k = normalizePublishKey12(raw);
    if (!k) return '';
    if (!needSet.has(k)) {
      needSet.add(k);
      needList.push(k);
      byKey[k] = { status: 'pending', ackDec: null, ackHex: '' };
      if (earlyBuf[k]) {
        applyAck(k, earlyBuf[k]);
        delete earlyBuf[k];
      }
    }
    return k;
  };

  (watchKeys || []).forEach((k) => ensureKey(k));

  let rxTopics = [...new Set((rxTopicsIn || []).map((t) => String(t || '').trim()).filter(Boolean))];

  const mergeRxTopics = (more) => {
    const next = [...rxTopics];
    (more || []).forEach((t) => {
      const s = String(t || '').trim();
      if (s && !next.includes(s)) next.push(s);
    });
    rxTopics = next;
  };

  if (!rxTopics.length && typeof getMqttMappingByMeter === 'function' && needList.length) {
    for (let i = 0; i < needList.length; i++) {
      try {
        const mapRes = await getMqttMappingByMeter(needList[i]);
        const rx = String(mapRes?.data?.rx_topic || '').trim();
        if (rx) mergeRxTopics([rx]);
        mergeRxTopics(rxTopicsForHttpPoll(mapRes?.data || {}));
      } catch (_) {}
    }
  }

  const unsettled = () => needList.filter((k) => byKey[k] && byKey[k].status === 'pending');

  const ingest = (raw) => {
    // 不限当前 need：先收下任意 52，已知目标直接落盘，未知进 earlyBuf
    const p = parseMqtt52RebootResponse(raw, []);
    if (!p || !p.key) return;
    if (byKey[p.key]) {
      applyAck(p.key, p);
      return;
    }
    if (!earlyBuf[p.key]) {
      earlyBuf[p.key] = {
        ok: p.ok,
        ackDec: p.ackDec,
        ackHex: p.ackHex || '',
      };
    }
  };

  let wsHandle = null;
  const openWs = async () => {
    if (!useWebSocket || !rxTopics[0]) return;
    try {
      wsHandle?.close();
    } catch (_) {}
    wsHandle = createMqttWS(
      rxTopics[0],
      (_topic, msg, hex) => {
        ingest(String(hex || msg || '').trim());
      },
      { extraRxTopics: rxTopics.slice(1) }
    );
    try {
      await Promise.race([wsHandle.ready, sleep(3000)]);
    } catch (_) {
      wsHandle = null;
    }
  };

  await openWs();

  const pollEvery = Math.max(400, Number(httpPollMs) || 600);
  const lastRawByKey = {};
  let deadline =
    Date.now() + (Number(waitMs) > 0 ? Number(waitMs) : YJ_REMOTE_REBOOT_ACK_WAIT_MS);

  try {
    if (typeof afterListenReady === 'function') {
      const more = await afterListenReady({ publishAt, rxTopics: rxTopics.slice() });
      if (typeof shouldAbort === 'function' && shouldAbort()) {
        return { byKey };
      }
      const prevTopicCount = rxTopics.length;
      (more?.watchKeys || []).forEach((k) => ensureKey(k));
      mergeRxTopics(more?.rxTopics || []);
      if (rxTopics.length > prevTopicCount) {
        await openWs();
      }
      // 下发完成后再起算等待窗口，避免接口耗时吃掉回包时间
      deadline =
        Date.now() + (Number(waitMs) > 0 ? Number(waitMs) : YJ_REMOTE_REBOOT_ACK_WAIT_MS);
    }

    if (!needList.length) return { byKey };

    while (Date.now() < deadline) {
      if (typeof shouldAbort === 'function' && shouldAbort()) break;
      if (!unsettled().length) break;

      const pending = unsettled();
      // 轻量：每个 RX 主题 × 每个仍 pending 的表各 1 次（有几表轮几下，收齐即不再打）
      const pollJobs = [];
      for (let ti = 0; ti < rxTopics.length; ti++) {
        const topic = rxTopics[ti];
        for (let ki = 0; ki < pending.length; ki++) {
          const mk = pending[ki];
          pollJobs.push(
            (async () => {
              try {
                const pollOpts = buildMqttMessagePollOpts(mk, 'reboot', publishAt);
                const msgRes = await getMqttLatestMessage(topic, 'raw', pollOpts);
                return { mk, msgRes };
              } catch {
                return { mk, msgRes: null };
              }
            })()
          );
        }
      }
      if (pollJobs.length) {
        const pairs = await Promise.all(pollJobs);
        for (let i = 0; i < pairs.length; i++) {
          const { mk, msgRes } = pairs[i];
          const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
          const lastRaw = lastRawByKey[mk] || '';
          const { accept, trimmed } = shouldAcceptPolledRaw({ raw, lastRaw, publishAt, msgRes });
          if (!accept) continue;
          lastRawByKey[mk] = trimmed;
          ingest(trimmed);
        }
      }
      if (!unsettled().length) break;
      await sleep(pollEvery);
    }
    unsettled().forEach((k) => {
      byKey[k] = { status: 'timeout', ackDec: null, ackHex: '' };
      emit(k);
    });
  } finally {
    try {
      wsHandle?.close();
    } catch (_) {}
    wsHandle = null;
  }
  return { byKey };
}

/**
 * 等一批 52 重启回包：mustAckKeys 全部 ACK=0 才成功；任一 ACK≠0 立即失败；超时失败。
 * （「重启当前电表」等强确认场景仍用此函数）
 */
export async function adminWaitReboot52Acks({
  mustAckKeys,
  rxTopics: rxTopicsIn,
  getMqttMappingByMeter,
  getMqttLatestMessage,
  waitMs = YJ_REMOTE_REBOOT_ACK_WAIT_MS,
  publishAt = Date.now(),
  useWebSocket = true,
  onProgress,
}) {
  const need = [...new Set((mustAckKeys || []).map((k) => normalizePublishKey12(k)).filter(Boolean))];
  if (!need.length) {
    throw new Error('无待确认重启目标');
  }
  const { byKey } = await adminCollectReboot52Statuses({
    watchKeys: need,
    rxTopics: rxTopicsIn,
    getMqttMappingByMeter,
    getMqttLatestMessage,
    waitMs,
    publishAt,
    useWebSocket,
    onUpdate: ({ byKey: snap }) => {
      if (typeof onProgress !== 'function') return;
      const got = need.filter((k) => snap[k]?.status === 'ok');
      onProgress({ need: need.slice(), got });
    },
  });
  for (let i = 0; i < need.length; i++) {
    const k = need[i];
    const st = byKey[k];
    if (st?.status === 'fail') {
      throw new Error(`表 ${k} 重启回包失败(ACK=${st.ackHex || '-'})`);
    }
    if (st?.status !== 'ok') {
      const missing = need.filter((x) => byKey[x]?.status !== 'ok');
      throw new Error(`等待重启回包超时（未确认：${missing.join(',') || '-'}）`);
    }
  }
  return { ok: true, got: need.slice(), need };
}

/**
 * 重启当前电表（旧逻辑）：先听再对本表 publish-by-meter 发 52，等本表 ACK=0。
 */
export async function adminRemoteRebootCurrent({
  device,
  getMqttMappingByMeter,
  mqttPublishByMeter,
  getMqttLatestMessage,
  useWebSocket = true,
}) {
  const publishKey = getAdminMeterPublishKey(device);
  if (!publishKey) throw new Error('无法确定表号或 MAC，请检查设备档案');

  const mapRes = await getMqttMappingByMeter(publishKey);
  if (!mapRes?.success || !mapRes?.data?.rx_topic) {
    throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
  }
  const mapData = mapRes.data;
  const waitMs = YJ_REMOTE_REBOOT_ACK_WAIT_MS;
  const isBleDirect = remotePayloadUseBleDirect(publishKey);
  const payload = buildFinalPayload(buildRemoteRebootHex(publishKey, isBleDirect), true);
  const publishAt = Date.now() - 500;
  const key12 = normalizePublishKey12(publishKey);
  const rxTopics = rxTopicsForHttpPoll(mapData);

  const tryPublish = async (mk) => {
    const pubRes = await mqttPublishByMeter({ meter_no: mk, payload, qos: 1 });
    if (!pubRes?.success) {
      throw new Error(pubRes?.msg || pubRes?.error || '重启指令发送失败');
    }
  };

  const { byKey } = await adminCollectReboot52Statuses({
    watchKeys: [key12],
    rxTopics,
    getMqttMappingByMeter,
    getMqttLatestMessage,
    waitMs,
    publishAt,
    useWebSocket,
    httpPollMs: 600,
    afterListenReady: async () => {
      try {
        await tryPublish(publishKey);
      } catch (e) {
        const msg = String(e?.message || e);
        const alt = String(device?.current_meter_no || device?.factory_meter_no || '').trim();
        if (/未找到该表号/.test(msg) && alt && alt.toLowerCase() !== String(publishKey).trim().toLowerCase()) {
          await tryPublish(alt);
        } else {
          throw e;
        }
      }
      return { watchKeys: [key12], rxTopics };
    },
  });

  const st = byKey[key12];
  if (st?.status === 'fail') {
    throw new Error(`表 ${key12} 重启回包失败(ACK=${st.ackHex || '-'})`);
  }
  if (st?.status !== 'ok') {
    throw new Error(`等待重启回包超时（未确认：${key12}）`);
  }
  return { publishKey: key12, payload };
}
